import { SearchParams } from '@components/CustomerSearch/components/CustomerSearchBox/CustomerSearchBox';
import { valtioDevTools } from '@lib/utils/devTools';
import { proxy, subscribe } from 'valtio';
import { IdName } from '@lib/utils/id-name';
import type { AgentCallerIdDetails } from '@components/OutboundCallerIdSelection/AgentCallerId/types';
import type { DeferredSelection } from '@modules/TasksModule/types';
import { isBookingRetentionEnabled } from './lib/isBookingRetentionEnabled';

type InboxState = {
  selectedTaskId: string | undefined;
  selectedTenantForOutboundCall: IdName | null;
  selectedCallerIdForOutboundCall: Record<string, AgentCallerIdDetails>;
  previousSelectedTaskId: string | undefined;
  missedCallsForQueue: Record<string, number>;
  missedCallsDid: number;
  showClassificationModalForTaskId: string;
  home: {
    searchParams?: SearchParams;
    selectedCustomer?: {
      tenantId: string;
      customerId: string;
    };
  };
  speedToLead: {
    isClaimingTask: boolean;
  };
};

export const defaultState = {
  selectedTaskId: undefined,
  selectedTenantForOutboundCall: null,
  selectedCallerIdForOutboundCall: {},
  previousSelectedTaskId: undefined,
  missedCallsForQueue: {},
  missedCallsDid: 0,
  showClassificationModalForTaskId: '',
  home: {
    searchParams: {
      name: '',
      phone: '',
      street: '',
      unit: '',
      zip: '',
    },
    selectedCustomer: undefined,
  },
};

export function getInitialState(): InboxState {
  const stored = localStorage.getItem('inbox');
  // callBarPositionDeltas belonged to the removed draggable call bar. Drop it
  // so the next save writes it out of localStorage.
  const { callBarPositionDeltas: _legacyCallBarPositionDeltas, ...state } =
    stored ? JSON.parse(stored) : defaultState;

  return {
    ...state,
    home: state.home
      ? { ...defaultState.home, ...state.home }
      : defaultState.home,
    missedExternalCallsForQueue: state.missedExternalCallsForQueue,
    selectedTenantForOutboundCall:
      state.selectedTenantForOutboundCall ??
      defaultState.selectedTenantForOutboundCall,
    selectedCallerIdForOutboundCall:
      state.selectedCallerIdForOutboundCall ??
      defaultState.selectedCallerIdForOutboundCall,
  };
}

export const inboxState = proxy<InboxState>(getInitialState());

valtioDevTools(inboxState, 'inboxState');

export const resetInboxState = () => {
  selectionHold = undefined;
  inboxState.selectedTenantForOutboundCall = null;
  inboxState.selectedCallerIdForOutboundCall = {};
  inboxState.selectedTaskId = undefined;
  inboxState.previousSelectedTaskId = undefined;
  inboxState.missedCallsForQueue = {};
  inboxState.missedCallsDid = 0;
  inboxState.showClassificationModalForTaskId = '';
  inboxState.home = {
    searchParams: {
      name: '',
      phone: '',
      street: '',
      unit: '',
      zip: '',
    },
    selectedCustomer: undefined,
  };
  inboxState.speedToLead = {
    isClaimingTask: false,
  };
};

type SelectionHold = {
  token: number;
  // undefined holds selection on the home view
  taskId: string | undefined;
  // every request made during the hold, oldest first
  deferred: (string | undefined)[];
};

// Module-level on purpose: inboxState is persisted to localStorage, and a
// reload must never restore a hold with no handshake behind it.
let selectionHold: SelectionHold | undefined;
let lastHoldToken = 0;

const applySelection = (taskId: string | undefined) => {
  let next = taskId;
  if (isBookingRetentionEnabled()) {
    // '' and undefined both mean the home view
    next = taskId === '' ? undefined : taskId;
    // Re-selecting the selected card (an incoming call's accept, an unhold, a
    // click on the card already shown) changes nothing. Recording it as its
    // own previous selection would make Close on it lose the card the CSR was
    // on (REQ-CTL-BOOK-002 AC-9). Off, restorePrevSelectedTaskId's "same as
    // selected, go home" branch relies on that record.
    if (next === inboxState.selectedTaskId) {
      return;
    }
  }
  inboxState.previousSelectedTaskId = inboxState.selectedTaskId;
  inboxState.selectedTaskId = next;
};

const setSelectedTaskId = (taskId: string | undefined) => {
  // the held task itself passes through; anything else waits for the release
  const requested = taskId === '' ? undefined : taskId;
  if (selectionHold && requested !== selectionHold.taskId) {
    selectionHold.deferred.push(requested);
    return;
  }
  applySelection(taskId);
};

/**
 * Freezes selection on `taskId` (`undefined` for the home view) while a Close
 * or leave discard prompt is open. A newer hold takes over from an older one.
 */
const holdSelection = (taskId: string | undefined): number => {
  lastHoldToken += 1;
  selectionHold = {
    token: lastHoldToken,
    taskId,
    deferred: selectionHold?.deferred ?? [],
  };
  return lastHoldToken;
};

/**
 * Clears the hold if `token` still owns it and returns the deferred request
 * without applying it; the caller applies it. A stale token is a no-op.
 */
const releaseSelection = (token: number): DeferredSelection | undefined => {
  if (selectionHold?.token !== token) {
    return undefined;
  }
  const { deferred } = selectionHold;
  selectionHold = undefined;
  if (deferred.length === 0) {
    return undefined;
  }
  const fallbacks = deferred.slice(0, -1);
  return {
    target: deferred[deferred.length - 1],
    ...(fallbacks.length > 0 ? { fallbacks } : {}),
  };
};

/** A CSR click: bypasses the hold and drops any deferred request. */
const selectTaskByUser = (taskId: string | undefined) => {
  if (selectionHold) {
    selectionHold.deferred = [];
  }
  applySelection(taskId === '' ? undefined : taskId);
};

const restorePrevSelectedTaskId = () => {
  if (selectionHold) {
    // the change a restore would undo never reached the screen: drop only
    // that one, so a call accepted earlier in the hold keeps its request
    selectionHold.deferred.pop();
    return;
  }
  // if previousSelectedTaskId is the same as selectedTaskId, then clear it and show the home inbox
  inboxState.selectedTaskId =
    inboxState.selectedTaskId === inboxState.previousSelectedTaskId
      ? undefined
      : inboxState.previousSelectedTaskId;
  inboxState.previousSelectedTaskId = undefined;
};

/**
 * Moves selection (and the previous selection) from `oldId` to `newId` when a
 * task is re-keyed, without the side effects of setSelectedTaskId, which would
 * leave previousSelectedTaskId pointing at the deleted id.
 */
const replaceTaskId = (oldId: string, newId: string) => {
  if (inboxState.selectedTaskId === oldId) {
    inboxState.selectedTaskId = newId;
  }
  if (inboxState.previousSelectedTaskId === oldId) {
    inboxState.previousSelectedTaskId = newId;
  }
  if (selectionHold?.taskId === oldId) {
    selectionHold.taskId = newId;
  }
  if (selectionHold) {
    selectionHold.deferred = selectionHold.deferred.map((target) =>
      target === oldId ? newId : target,
    );
  }
};

/** Forgets the previous selection if it names `taskId`, which is no longer a valid restore target. */
const clearPreviousSelectedTaskId = (taskId: string) => {
  if (inboxState.previousSelectedTaskId === taskId) {
    inboxState.previousSelectedTaskId = undefined;
  }
};

// The Inbox home row is a CSR click, so it bypasses a selection hold.
const setHomeInbox = () => {
  selectTaskByUser(undefined);
};

const setInboxHomeSearchParams = (searchParams: SearchParams) => {
  if (!inboxState.home) {
    inboxState.home = {};
  }
  inboxState.home.searchParams = searchParams;
};

const setInboxHomeCustomer = (selectedCustomer: any) => {
  if (!inboxState.home) {
    inboxState.home = {};
  }
  inboxState.home.selectedCustomer = selectedCustomer;
};

const setTenantForOutboundCall = (tenant: IdName | null) => {
  inboxState.selectedTenantForOutboundCall = tenant;
};

const setCallerIdForOutboundCall = (
  tenantKey: string,
  callerIdDetails: AgentCallerIdDetails,
) => {
  inboxState.selectedCallerIdForOutboundCall[tenantKey] = callerIdDetails;
};

export const inboxActions = {
  setSelectedTaskId,
  setHomeInbox,
  restorePrevSelectedTaskId,
  replaceTaskId,
  clearPreviousSelectedTaskId,
  holdSelection,
  releaseSelection,
  selectTaskByUser,
  setInboxHomeSearchParams,
  setInboxHomeCustomer,
  setTenantForOutboundCall,
  setCallerIdForOutboundCall,
};

subscribe(inboxState, () => {
  localStorage.setItem('inbox', JSON.stringify(inboxState));
});
